import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ENDPOINTS, API_URL, productPath } from '../config';

export default function RelatedProducts({ currentProductId, brandSlug, categorySlug }) {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);

    useEffect(() => {
        const fetchRelated = async () => {
            try {
                // Try fetching by brand first, fall back to category
                const slug = brandSlug || categorySlug;
                const filterKey = brandSlug ? 'brand' : 'category';
                if (!slug) return;

                const res = await fetch(
                    `${ENDPOINTS.PRODUCTS.PUBLIC}?${filterKey}=${slug}&limit=12`
                );
                const data = await res.json();

                if (data?.isOk && data?.data?.products) {
                    // Exclude the current product
                    const filtered = data.data.products.filter(
                        (p) => p._id !== currentProductId
                    );
                    setProducts(filtered.slice(0, 6));
                }
            } catch (err) {
                console.error('Error fetching related products:', err);
            }
        };

        fetchRelated();
    }, [currentProductId, brandSlug, categorySlug]);

    const handleProductClick = (slug) => {
        navigate(productPath(slug));
        window.scrollTo(0, 0);
    };

    if (products.length === 0) return null;

    return (
        <section className="mb-12 mt-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">You May Also Like</h2>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {products.map((product) => {
                    const imageUrl = product.primaryImage
                        ? `${API_URL}/${product.primaryImage}`
                        : null;

                    return (
                        <div
                            key={product._id}
                            onClick={() => handleProductClick(
                                product.variantSlug
                                || product.variant?.slug
                                || product.variants?.[0]?.slug
                                || product.slug
                            )}
                            className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                        >
                            {/* Image */}
                            <div className="relative w-full h-40 bg-muted/50 overflow-hidden">
                                {imageUrl ? (
                                    <img
                                        src={imageUrl}
                                        alt={product.name}
                                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <i className="ri-image-line text-3xl text-muted-foreground/40"></i>
                                    </div>
                                )}
                                {product.discountPercentage > 0 && (
                                    <span className="absolute top-2 left-2 px-2 py-1 bg-primary text-white text-xs font-bold rounded whitespace-nowrap">
                                        {product.discountPercentage}% OFF
                                    </span>
                                )}
                            </div>

                            {/* Content */}
                            <div className="p-3">
                                {product.brand?.name && (
                                    <p className="text-xs text-muted-foreground mb-1">{product.brand.name}</p>
                                )}
                                <h3 className="text-sm font-semibold text-foreground mb-2 line-clamp-2 min-h-[40px]">
                                    {product.name}
                                </h3>

                                {/* Price */}
                                <div className="flex flex-col gap-1">
                                    <span className="text-base font-bold text-foreground">
                                        KSh {product.lowestPrice?.toLocaleString() || 0}
                                    </span>
                                    {product.discountPercentage > 0 && product.costPrice > 0 && (
                                        <span className="text-xs text-muted-foreground line-through">
                                            KSh {product.costPrice.toLocaleString()}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
